import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  commonUtils,
  useDialogs,
  useNotifications,
  useEditorStore,
  useFlowBuilder2UpdateFlowStepMutation,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';

export const useDeleteActivity = (
  options?: Parameters<typeof useFlowBuilder2UpdateFlowStepMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { confirm } = useDialogs();
  const notifications = useNotifications();

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);

  const { mutateAsync, isPending: isDeletingActivity } = useFlowBuilder2UpdateFlowStepMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      updateFlow((draft) => {
        draft.steps = data;
        return draft;
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.deleteActivityFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const deleteActivity = useCallback(
    async (args: { actionSetId: string; actionId: string; activityId: string }) => {
      const flow = getFlow();
      const actionSet = flow?.steps.find((step) => step.actionSetId === args.actionSetId);
      const action = actionSet?.actions.find((action) => action.id === args.actionId);
      const activity = action?.activities?.find((activity) => activity.id === args.activityId);

      if (flow && actionSet && action && activity) {
        const { confirmed } = await confirm(null, {
          title: t('prompts.areYouSureToDeleteActivityName', {
            activityName: activity.displayName,
          }),
          variant: 'delete',
        });

        if (confirmed) {
          return mutateAsync({
            flowId: flow.id,
            flowStepId: actionSet.id,
            original: actionSet,
            data: {
              id: actionSet.id,
              actions: actionSet.actions.map((action) => {
                if (action.id === args.actionId && action.activities) {
                  return {
                    ...action,
                    activities: action.activities.filter((activity) => {
                      return activity.id !== args.activityId;
                    }),
                  };
                }
                return action;
              }),
            },
          }).catch();
        }
      }
    },
    [t, confirm, getFlow, mutateAsync],
  );

  return { deleteActivity, isDeletingActivity };
};
